import React from "react";
import { Helmet } from "react-helmet";
import Header from "../Header";
import Footer from "../Footer";
import "./VfxTechnicalPages.css";

const pages = {
    particles: {
        slug: "game-particle-effects",
        eyebrow: "Game Particle Effects",
        title: "Particle Effects for Game Combat, Rewards, Magic, UI & Environments",
        description: "Oengines Games LLP creates game particle effects for combat hits, spells, explosions, rewards, power-ups, trails, UI feedback, environment ambience, and mobile game moments.",
        keywords: "game particle effects, VFX for games, particle effects for Unity games, mobile game VFX, combat effects, reward effects, game visual effects",
        layout: "particles",
        stats: ["Combat", "Magic", "Rewards", "Trails"],
        visual: "particles",
        services: [
            ["Combat VFX", "Hit sparks, slashes, muzzle flashes, explosions, impact bursts, damage feedback, and ability effects."],
            ["Reward Effects", "Coin bursts, chest openings, unlock effects, rarity reveals, level-up effects, and store reward moments."],
            ["Environment VFX", "Fire, smoke, fog, dust, rain, snow, portals, ambient particles, and scene atmosphere for game worlds."],
            ["Performance-Aware VFX", "Particle count planning, texture usage, mobile constraints, timing, readability, and game-engine handoff notes."]
        ],
        process: [
            ["Effect Brief", "Define gameplay trigger, camera view, timing, style, color, and platform constraints."],
            ["Motion Prototype", "Block the effect timing, shape, burst, trail, and animation rhythm."],
            ["Polish Pass", "Tune glow, scale, fade, texture, contrast, and readability."],
            ["Game Handoff", "Deliver implementation notes, export assets, timing values, and optimization guidance."]
        ],
        deliverables: ["Effect style frames", "Particle texture assets", "Timing notes", "Sprite sheets", "Glow/trail assets", "Optimization notes", "Engine handoff", "Reference previews"],
        faqs: [
            ["Can you create particle effects for mobile games?", "Yes. We plan particle count, texture size, readability, and performance for mobile gameplay."],
            ["Can you create combat and reward VFX?", "Yes. We create hit effects, spells, explosions, coin bursts, unlocks, level-ups, and reward reveals."],
            ["Do you consider performance?", "Yes. We keep effect duration, overdraw, particle count, and texture usage practical for games."]
        ]
    },
    shaders: {
        slug: "game-shader-development",
        eyebrow: "Game Shader Development",
        title: "Shader Development for Stylized Game Visuals, Materials & Effects",
        description: "We create shader direction and technical art support for game materials, stylized surfaces, dissolve effects, outlines, holograms, water, energy, UI effects, and real-time game visuals.",
        keywords: "game shader development, stylized game shaders, technical art shaders, dissolve shader, outline shader, real-time material effects, game material shader",
        layout: "shader",
        stats: ["Materials", "Outlines", "Dissolve", "Energy"],
        visual: "shader",
        services: [
            ["Stylized Materials", "Toon looks, rim lights, outlines, gradient materials, color ramps, and stylized game surface direction."],
            ["Gameplay Effects", "Dissolve, damage flash, shield, stealth, hologram, energy, portal, scanline, and ability-driven visual states."],
            ["Environment Shaders", "Water, lava, force fields, fog planes, animated surfaces, scrolling textures, and interactive material ideas."],
            ["Technical Handoff", "Shader behavior notes, texture channel planning, performance guidance, and implementation-ready direction."]
        ],
        process: [
            ["Visual Goal", "Define the shader use case, gameplay feedback, style target, and engine limits."],
            ["Material Logic", "Plan masks, color channels, textures, animation, and exposed controls."],
            ["Prototype Review", "Check readability, contrast, lighting response, and target device cost."],
            ["Implementation Notes", "Prepare values, texture packing notes, usage guidance, and optimization recommendations."]
        ],
        deliverables: ["Shader notes", "Material style guide", "Mask guidance", "Texture channel plan", "Effect timing", "Performance notes", "Prototype references", "Handoff specs"],
        faqs: [
            ["Can shaders support stylized games?", "Yes. We plan toon, outline, ramp, glow, dissolve, energy, and stylized material effects for games."],
            ["Can shader work be optimized for mobile?", "Yes. We consider texture channels, sampling cost, overdraw, and simpler fallback directions."],
            ["Can shaders support gameplay feedback?", "Yes. Shaders can show damage, shields, stealth, power-ups, scan effects, and ability states."]
        ]
    },
    lighting: {
        slug: "game-lighting",
        eyebrow: "Game Lighting",
        title: "Game Lighting for Mood, Readability, Levels, UI Focus & Cinematic Moments",
        description: "Oengines creates game lighting direction for levels, characters, arenas, menus, cutscenes, store screens, battle scenes, mobile games, and real-time playable environments.",
        keywords: "game lighting, lighting for games, real-time game lighting, mobile game lighting, level lighting, game environment lighting, cinematic game lighting",
        layout: "lighting",
        stats: ["Mood", "Focus", "Levels", "Cinematics"],
        visual: "lighting",
        services: [
            ["Level Lighting", "Readable paths, gameplay focus, enemy visibility, landmark lighting, scene mood, and environment hierarchy."],
            ["Character & Item Lighting", "Hero highlights, store previews, reward screens, item readability, rarity glow, and character presentation."],
            ["Cinematic Lighting", "Boss intros, cutscenes, trailers, dramatic reveals, environment shots, and story moments."],
            ["Performance Planning", "Light count, bake direction, shadow cost, mobile constraints, ambience, and engine-friendly lighting notes."]
        ],
        process: [
            ["Mood Target", "Define color mood, gameplay readability, camera angle, and scene purpose."],
            ["Lighting Blockout", "Place key, fill, rim, accent, UI focus, and environment light ideas."],
            ["Readability Pass", "Check player route, target focus, enemy visibility, and contrast."],
            ["Performance Review", "Prepare bake, shadow, light count, and mobile optimization recommendations."]
        ],
        deliverables: ["Lighting mood board", "Scene lighting notes", "Color keys", "Gameplay readability notes", "Store lighting direction", "Cinematic lighting guide", "Optimization notes", "Handoff specs"],
        faqs: [
            ["Can lighting improve gameplay readability?", "Yes. Lighting can guide player focus, show routes, separate enemies, and improve scene hierarchy."],
            ["Can you plan lighting for mobile games?", "Yes. We consider baked light, simple dynamic lights, shadow cost, and device performance."],
            ["Can you create lighting for store or reward screens?", "Yes. We plan item highlights, rarity glow, reward focus, and character presentation lighting."]
        ]
    },
    optimization: {
        slug: "game-art-optimization",
        eyebrow: "Game Art Optimization",
        title: "Game Art Optimization for Assets, VFX, UI, Materials & Real-Time Performance",
        description: "We optimize game art direction for mobile, web, PC, and console projects, including assets, textures, UI, VFX, materials, draw calls, memory, and real-time performance planning.",
        keywords: "game art optimization, mobile game optimization, game asset optimization, VFX optimization, texture optimization, technical art optimization, real-time game performance",
        layout: "optimization",
        stats: ["Memory", "Draw Calls", "VFX Cost", "Textures"],
        visual: "optimization",
        services: [
            ["Asset Optimization", "Polygon budgets, LOD planning, mesh cleanup, material grouping, atlas strategy, and platform-aware asset review."],
            ["Texture Optimization", "Texture size planning, channel packing, compression guidance, UI atlas planning, and memory-aware delivery."],
            ["VFX Optimization", "Particle count, overdraw reduction, texture reuse, effect duration, screen coverage, and mobile-friendly VFX notes."],
            ["Technical Art Audit", "Scene cost review, draw-call awareness, shader complexity, UI asset weight, and production handoff recommendations."]
        ],
        process: [
            ["Performance Audit", "Review target devices, asset list, scene cost, memory pressure, and gameplay requirements."],
            ["Budget Planning", "Define texture, mesh, shader, VFX, UI, and lighting budgets."],
            ["Optimization Pass", "Reduce cost while preserving gameplay readability and visual appeal."],
            ["Handoff Guide", "Prepare recommendations developers and artists can follow during production."]
        ],
        deliverables: ["Optimization checklist", "Asset budget notes", "Texture plan", "VFX cost notes", "Shader guidance", "LOD guidance", "UI atlas notes", "Performance handoff"],
        faqs: [
            ["Can you optimize art for mobile games?", "Yes. We focus on memory, texture sizes, draw calls, overdraw, shader cost, and asset budgets."],
            ["Can optimization keep the game looking good?", "Yes. The goal is to reduce cost while protecting visual clarity and player experience."],
            ["Can you audit existing game art?", "Yes. We can review assets, UI, VFX, materials, and scene structure to suggest improvements."]
        ]
    }
};

function VfxTechnicalPage({ page }) {
    const data = pages[page];
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": data.eyebrow,
        "provider": {
            "@type": "Organization",
            "name": "Oengines Games LLP",
            "url": "https://www.oengines.com"
        },
        "serviceType": data.eyebrow,
        "areaServed": "Worldwide",
        "description": data.description
    };

    return (
        <>
            <Helmet>
                <title>{data.title} | Oengines Games LLP</title>
                <meta name="description" content={data.description} />
                <meta name="keywords" content={data.keywords} />
                <link rel="canonical" href={`https://www.oengines.com/${data.slug}`} />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            </Helmet>
            <Header />
            <main className={`vfx-page vfx-${data.layout}`}>
                <section className="vfx-hero">
                    <div className="vfx-hero-grid">
                        <div className="vfx-hero-copy">
                            <span>{data.eyebrow}</span>
                            <h1>{data.title}</h1>
                            <p>{data.description}</p>
                            <div className="vfx-actions">
                                <a href="/contact" className="as-btn">Discuss Technical Art <i className="fa-regular fa-arrow-right ms-2"></i></a>
                                <a href="tel:+912614054978" className="vfx-call">Call: (0261) - 4054978</a>
                            </div>
                        </div>
                        <div className="vfx-visual" aria-hidden="true">
                            {data.visual === "particles" && (
                                <div className="vfx-particle-stage">
                                    {Array.from({ length: 20 }).map((_, index) => <i key={index} />)}
                                    <strong>BURST</strong>
                                </div>
                            )}
                            {data.visual === "lighting" && (
                                <div className="vfx-light-stage">
                                    <i className="beam beam-one" />
                                    <i className="beam beam-two" />
                                    <i className="beam beam-three" />
                                    <strong>LIGHTING PASS</strong>
                                </div>
                            )}
                            {data.visual === "shader" && (
                                <div className="vfx-shader-stage">
                                    <i /><i /><i /><i />
                                    <strong>SHADER STACK</strong>
                                </div>
                            )}
                            {data.visual === "optimization" && (
                                <div className="vfx-optimization-stage">
                                    <i /><i /><i /><i /><i />
                                    <strong>PERFORMANCE HUD</strong>
                                </div>
                            )}
                        </div>
                        <div className="vfx-stat-row">
                            {data.stats.map((item) => <span key={item}>{item}</span>)}
                        </div>
                    </div>
                </section>

                <section className="vfx-capabilities space">
                    <div className="container-fluid px-xxl-5">
                        <div className="vfx-heading">
                            <span>Game VFX & Technical Art</span>
                            <h2>Built for Gameplay Clarity, Real-Time Performance, and Strong Visual Feedback</h2>
                        </div>
                        <div className="vfx-card-grid">
                            {data.services.map(([title, text], index) => (
                                <article key={title} className={index === 0 ? "featured" : ""}>
                                    <strong>{String(index + 1).padStart(2, "0")}</strong>
                                    <h3>{title}</h3>
                                    <p>{text}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="vfx-process space">
                    <div className="container">
                        <div className="vfx-process-grid">
                            <div className="vfx-process-title">
                                <span>Production Flow</span>
                                <h2>{data.eyebrow} Workflow</h2>
                                <p>Every technical art page is planned around gameplay triggers, target platform, readability, performance cost, and developer handoff.</p>
                            </div>
                            <div className="vfx-process-list">
                                {data.process.map(([title, text], index) => (
                                    <article key={title}>
                                        <strong>{String(index + 1).padStart(2, "0")}</strong>
                                        <div>
                                            <h3>{title}</h3>
                                            <p>{text}</p>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="vfx-deliverables space">
                    <div className="container">
                        <div className="vfx-deliverable-grid">
                            <div>
                                <span>Deliverables</span>
                                <h2>Technical Art Assets and Notes for Game Teams</h2>
                                <p>We keep deliverables practical for artists, developers, designers, and producers who need clear implementation direction.</p>
                            </div>
                            <div className="vfx-deliverable-list">
                                {data.deliverables.map((item) => <span key={item}>{item}</span>)}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="vfx-faq space">
                    <div className="container">
                        <div className="vfx-faq-grid">
                            <div className="vfx-faq-title">
                                <span>FAQ</span>
                                <h2>{data.eyebrow} Questions</h2>
                                <a href="/contact" className="as-btn">Start VFX Project <i className="fa-regular fa-arrow-right ms-2"></i></a>
                            </div>
                            <div className="vfx-faq-list">
                                {data.faqs.map(([question, answer]) => (
                                    <article key={question}>
                                        <h3>{question}</h3>
                                        <p>{answer}</p>
                                    </article>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}

export const GameParticleEffects = () => <VfxTechnicalPage page="particles" />;
export const GameShaderDevelopment = () => <VfxTechnicalPage page="shaders" />;
export const GameLighting = () => <VfxTechnicalPage page="lighting" />;
export const GameArtOptimization = () => <VfxTechnicalPage page="optimization" />;
